import { useCallback, useEffect, useState } from 'react';
import type { Accounts, Providers } from '@accounts/domain';
import { Refusal } from '@accounts/transport';

/**
 * The sign-in methods this installation offers, read once when the screen opens.
 *
 * The read ends in the methods or in `unread`, the reason they could not be read — Accounts not
 * answering, which the screen says once (`Outage`, D163) and reads again by itself, never as "nothing is
 * configured".
 */
export function useMethods(accounts: Accounts) {
	const [providers, setProviders] = useState<Providers>();
	const [reading, setReading] = useState(true);
	const [unread, setUnread] = useState<Refusal>();

	// Whether Accounts answered, which takes the read out of the screen's outage
	const load = useCallback(async () => {
		setReading(true);
		setUnread(Refusal.kept);
		try {
			setProviders(await accounts.providers());
			setUnread(undefined);
			return true;
		} catch (error) {
			setProviders(undefined);
			const given = Refusal.is(error) ? error : new Refusal('UNREACHABLE', 'Unexpected');
			setUnread(given);
			return !given.unreachable;
		} finally {
			setReading(false);
		}
	}, [accounts]);

	useEffect(() => {
		void load();
	}, [load]);

	return { providers, reading, unread, load };
}
